import OrderMap from "./OrderMap";
import OrderSummary from "./OrderSummary";
import {
  SummaryPeriod,
  useFetchCODCollectionSummary,
  useFetchCityRegionBreakdown,
  useFetchDriverWiseOrderStatus,
  useFetchMerchantWiseShipmentDetails,
} from "../../../apis/dashboardApis";
import CustomTable from "./CustomMainTable";
import ViewCitiesPriceMatrixModal from "./ViewCitiesPriceMatrixModal";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import OrderStatusChart from "./OrderStatusChart";
import StatusTrendChart from "./StatusTrendChart";
import CodByMarchent from "./CodByMarchent";
import FleetSummary from "./FleetSummary";

const columns = [
  {
    key: "sn",
    title: "SN",
  },
  {
    key: "driver",
    title: "Driver",
    render: (value: string) => (
      <span className="font-semibold text-[#212121]/70">{value}</span>
    ),
  },

  {
    key: "collected",
    title: "COD Collected",
  },

  {
    key: "remitted",
    title: "Remitted",
    render: (value: string) => <span className="text-[#22C55E]">{value}</span>,
  },

  {
    key: "pending",
    title: "Pending",
    render: (value: string) => <span className="text-[#EF4444]">{value}</span>,
  },

  {
    key: "rate",
    title: "Rate",
    render: (value: number) => (
      <div className="flex items-center gap-3">
        <div className="h-2 w-28 overflow-hidden rounded-full bg-[#D2D2D2]">
          <div
            className="h-full rounded-full bg-[#22C55E]"
            style={{ width: `${value}%` }}
          />
        </div>

        <span>{value}%</span>
      </div>
    ),
  },
];

const getStatusColor = (status: string) => {
  switch (status) {
    case "Active":
      return "bg-green-500/20 text-green-400 px-2 py-1.5 rounded-full";

    case "Returning":
      return "bg-blue-500/20 text-blue-400 px-2 py-1.5 rounded-full";

    case "On Break":
      return "bg-purple-500/20 text-purple-400 px-2 py-1.5 rounded-full";

    default:
      return "bg-red-500/20 text-red-400 px-2 py-1.5 rounded-full";
  }
};

const columns1 = [
  {
    key: "sn",
    title: "SN",
  },
  {
    key: "driver",
    title: "Driver",
    render: (value: string) => (
      <span className="font-semibold text-[#212121]/70">{value}</span>
    ),
  },

  {
    key: "assigned",
    title: "Assigned",
  },

  {
    key: "ofd",
    title: "OFD",
    render: (value: string) => (
      <span className="bg-[#227cc5]/20 rounded-full p-2 text-[#227cc5]">
        {value}
      </span>
    ),
  },
  {
    key: "delivered",
    title: "Delivered",
    render: (value: string) => (
      <span className="bg-[#22C55E]/20 rounded-full p-2 text-[#22C55E]">
        {value}
      </span>
    ),
  },

  {
    key: "notDelivered",
    title: "Not Del.",
    render: (value: string) => (
      <span className="bg-[#EF4444]/20 rounded-full p-2 text-[#EF4444]">
        {value}
      </span>
    ),
  },

  {
    key: "successRate",
    title: "Success %",
    render: (value: number) => (
      <div className="flex items-center gap-3">
        <div className="h-2 w-28 overflow-hidden rounded-full bg-[#D2D2D2]">
          <div
            className="h-full rounded-full bg-[#22C55E]"
            style={{ width: `${value}%` }}
          />
        </div>

        <span>{value}%</span>
      </div>
    ),
  },
  {
    key: "status",
    title: "Status",
    render: (value: string) => (
      <span className={getStatusColor(value)}>{value}</span>
    ),
  },
];

const columns2 = [
  {
    key: "sn",
    title: "SN",
  },
  {
    key: "merchant",
    title: "Merchant",
    render: (value: string) => (
      <span className="font-semibold text-[#212121]/70">{value}</span>
    ),
  },

  {
    key: "total",
    title: "Total",
  },

  {
    key: "ofd",
    title: "OFD",
    render: (value: string) => (
      <span className="bg-[#227cc5]/20 rounded-full p-2 text-[#227cc5]">
        {value}
      </span>
    ),
  },
  {
    key: "delivered",
    title: "Delivered",
    render: (value: string) => (
      <span className="bg-[#22C55E]/20 rounded-full p-2 text-[#22C55E]">
        {value}
      </span>
    ),
  },

  {
    key: "notDelivered",
    title: "Not Del.",
    render: (value: string) => (
      <span className="bg-[#EF4444]/20 rounded-full p-2 text-[#EF4444]">
        {value}
      </span>
    ),
  },

  {
    key: "successRate",
    title: "Success %",
    render: (value: number) => (
      <div className="flex items-center gap-3">
        <div className="h-2 w-28 overflow-hidden rounded-full bg-[#D2D2D2]">
          <div
            className="h-full rounded-full bg-[#22C55E]"
            style={{ width: `${value}%` }}
          />
        </div>

        <span>{value}%</span>
      </div>
    ),
  },
  {
    key: "cod",
    title: "COD (BDT)",
  },
];

export default function Overview({
  summaryType,
  startDate,
  endDate,
}: {
  summaryType?: SummaryPeriod;
  startDate?: string;
  endDate?: string;
}) {
  const [cityModalOpen, setCityModalOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string>("");

  // Fetch driver performance data inside component
  const { data: driverData, isLoading: driverLoading } =
    useFetchDriverWiseOrderStatus({
      type: summaryType ?? "Today",
      startDate,
      endDate,
      limit: 10,
    });

  // Map API response to table rows
  const mappedData =
    driverData?.map((d: any, idx: number) => ({
      sn: String(idx + 1).padStart(2, "0"),
      driver: d.driverName,
      assigned: d.assigned?.toString() ?? "0",
      ofd: d.ofd?.toString() ?? "0",
      delivered: d.delivered?.toString() ?? "0",
      notDelivered: d.notDelivered?.toString() ?? "0",
      successRate: d.successRate ?? 0,
      status: d.status ?? "Active",
    })) ?? [];

  // Fetch merchant shipment table data
  const { data: merchantData } = useFetchMerchantWiseShipmentDetails({
    type: summaryType ?? "Today",
    startDate,
    endDate,
    limit: 15,
  });

  // Map API response to table rows
  const data2 =
    merchantData?.map((m: any, idx: number) => ({
      sn: String(idx + 1).padStart(2, "0"),
      merchant: m.merchantName || m.merchant || "",
      total: m.total?.toString() ?? "0",
      ofd: m.ofd?.toString() ?? "0",
      delivered: m.delivered?.toString() ?? "0",
      notDelivered: m.notDelivered?.toString() ?? "0",
      successRate: m.successRate ?? 0,
      cod: m.cod?.toString() ?? "0",
    })) ?? [];

  const { data: codCollectionData } = useFetchCODCollectionSummary({
    type: summaryType ?? "Today",
    startDate,
    endDate,
    limit: 10,
  });

  const data =
    codCollectionData?.map((item: any, idx: number) => ({
      sn: String(idx + 1).padStart(2, "0"),
      driver: item.driverName ?? "",
      collected: item.collected?.toString() ?? "0",
      remitted: item.remitted?.toString() ?? "0",
      pending: item.pending?.toString() ?? "0",
      rate: item.rate ?? 0,
    })) ?? [];

  const { data: cityRegionData } = useFetchCityRegionBreakdown({
    type: summaryType ?? "Today",
    startDate,
    endDate,
    limit: 100,
  });

  const data3 =
    cityRegionData?.map((item: any) => ({
      city: item.city ?? "",
      region: item.region ?? "Others",
      total: item.total?.toString() ?? "0",
      ofd: item.ofd?.toString() ?? "0",
      delivered: item.delivered?.toString() ?? "0",
      notDelivered: item.notDelivered?.toString() ?? "0",
      successRate: item.successRate ?? 0,
      drivers: item.drivers?.toString() ?? "0",
    })) ?? [];

  const selectedRegionCities = data3.filter(
    (item: any) => item.region === selectedRegion,
  );

  const columns3 = [
    {
      key: "city",
      title: "City",
      render: (value: string) => (
        <span className="font-semibold text-[#212121]/70">{value}</span>
      ),
    },
    {
      key: "region",
      title: "Region",
      render: (value: string) => (
        <span
          onClick={() => {
            setSelectedRegion(value);
            setCityModalOpen(true);
          }}
          className="bg-purple-600/20 text-purple-500 px-2 py-1.5 rounded-full cursor-pointer"
        >
          {value}
        </span>
      ),
    },
    {
      key: "total",
      title: "Total",
    },

    {
      key: "ofd",
      title: "OFD",
      render: (value: string) => (
        <span className="bg-[#227cc5]/20 rounded-full p-2 text-[#227cc5]">
          {value}
        </span>
      ),
    },
    {
      key: "delivered",
      title: "Delivered",
      render: (value: string) => (
        <span className="bg-[#22C55E]/20 rounded-full p-2 text-[#22C55E]">
          {value}
        </span>
      ),
    },

    {
      key: "notDelivered",
      title: "Not Del.",
      render: (value: string) => (
        <span className="bg-[#EF4444]/20 rounded-full p-2 text-[#EF4444]">
          {value}
        </span>
      ),
    },
    {
      key: "drivers",
      title: "Drivers",
    },
    {
      key: "successRate",
      title: "Del. Rate",
      render: (value: number) => (
        <div className="flex items-center gap-3">
          <div className="h-2 w-28 overflow-hidden rounded-full bg-[#D2D2D2]">
            <div
              className="h-full rounded-full bg-[#22C55E]"
              style={{ width: `${value}%` }}
            />
          </div>

          <span>{value}%</span>
        </div>
      ),
    },
  ];
  const [, setSearchParams] = useSearchParams();

  return (
    <>
      <section className="grid grid-cols-12 gap-6">
        {/* Order Summary */}
        <div className="col-span-12 rounded-[32px] border border-[#ECECEC] bg-[#FAFAFA] p-6 shadow-sm">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 xl:col-span-9">
              <OrderSummary
                summaryType={summaryType}
                startDate={startDate}
                endDate={endDate}
              />
            </div>

            <div className="col-span-12 xl:col-span-3">
              <div className="h-full rounded-[28px] border border-[#ECECEC] bg-white p-6 shadow-sm">
                <OrderStatusChart
                  summaryType={summaryType}
                  startDate={startDate}
                  endDate={endDate}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Driver Analytics */}
        <div className="col-span-12 rounded-[32px] border border-[#ECECEC] bg-[#FAFAFA] p-6 shadow-sm">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 xl:col-span-4">
              <StatusTrendChart
                summaryType={summaryType}
                startDate={startDate}
                endDate={endDate}
              />
            </div>

            <div className="col-span-12 xl:col-span-8">
              <CustomTable
                className="bg-white border border-[#ECECEC] shadow-sm"
                title="Driver-wise Order Status"
                columns={columns1}
                data={driverLoading ? [] : mappedData}
              />
            </div>
          </div>
        </div>
        <div className="col-span-12 rounded-[32px] border border-[#ECECEC] bg-[#FAFAFA] p-6 shadow-sm">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 xl:col-span-8">
              <CustomTable
                className="bg-white border border-[#ECECEC] shadow-sm rounded-[32px]"
                title="Merchant-wise Shipment Details"
                columns={columns2}
                data={data2}
              />
            </div>
            <div className="col-span-12 xl:col-span-4">
              <FleetSummary
                summaryType={summaryType}
                startDate={startDate}
                endDate={endDate}
              />
            </div>
          </div>
        </div>

        {/* COD Analytics */}
        <div className="col-span-12 rounded-[32px] border border-[#ECECEC] bg-[#FAFAFA] p-6 shadow-sm">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 xl:col-span-4">
              <CodByMarchent
                summaryType={summaryType}
                startDate={startDate}
                endDate={endDate}
              />
            </div>
            <div className="col-span-12 xl:col-span-8">
              <CustomTable
                className="bg-white border border-[#ECECEC] shadow-sm"
                title="COD Collection Summary"
                columns={columns}
                data={data}
              />
            </div>
          </div>
        </div>

        {/* City Breakdown */}
        <div className="col-span-12 rounded-[32px] border border-[#ECECEC] bg-[#FAFAFA] p-6 shadow-sm">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 xl:col-span-5">
              <OrderMap
                summaryType={summaryType}
                startDate={startDate}
                endDate={endDate}
              />
            </div>

            <div className="col-span-12 xl:col-span-7">
              <CustomTable
                className="bg-white border border-[#ECECEC] shadow-sm"
                title="City & Region Breakdown"
                columns={columns3}
                data={data3}
              />
            </div>
          </div>
        </div>
      </section>
      <ViewCitiesPriceMatrixModal
        isOpen={cityModalOpen}
        onClose={() => setCityModalOpen(false)}
        region={selectedRegion}
        cities={selectedRegionCities}
      />
    </>
  );
}
